import { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import back from "@/assets/v1/back.jpg";
import React from 'react';
import { cardsImages } from '@/constants/cardsImages';
import cardsData from '@/constants/cards.json';

interface CardImage2Props {
    id: number;
    visible: boolean;
    width?: number;
    description?: any;
    opacity?: number;
    reversed?: boolean;
    flipped?: boolean;
    showFront?: boolean; // Controls whether to show front (true) or back (false) of the card
    onCardClick?: (card: {
        id: number;
        card_name: string;
        primary_meaning: string;
        interpretation: string;
    }) => void;
}

// Helper function to determine if a card should show its name
const shouldShowCardName = (id: number): boolean => {
    // Major Arcana (0-21)
    if (id <= 21) return true;

    // Court cards: Page, Knight, Queen, King
    const courtCardIds = [22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77];

    return courtCardIds.includes(id);
};

// Helper function to get card name
const getCardName = (id: number): string => {
    const card = cardsData.cards.find(card => card.id === id);
    return card ? card.card_name : '';
};

const CardImage2: React.FC<CardImage2Props> = React.memo(({
    id,
    visible,
    width = 140,
    description,
    onCardClick,
    opacity = 0,
    reversed = false,
    flipped = false,
    showFront = true,
}) => {
    const calculatedWidth = width;
    const height = calculatedWidth * (240 / 140);
    const descriptionRef = useRef(description);
    const [isAppeared, setIsAppeared] = useState(false);
    const [isFlipped, setIsFlipped] = useState(flipped);

    useEffect(() => {
        descriptionRef.current = description;
    }, [description]);

    // Sequential animation effect
    useEffect(() => {
        if (visible) {
            // Step 1: Card appears (opacity = 1) - immediate
            setIsAppeared(true);

            // Step 2 & 3: After 300ms, flip the card to show the image
            const flipTimer = setTimeout(() => {
                setIsFlipped(true);
            }, 300);

            return () => clearTimeout(flipTimer);
        } else {
            // Reset animation states when card becomes invisible
            setIsAppeared(false);
            setIsFlipped(false);
        }
    }, [visible]);
    const handleCardClick = () => {
        if (visible && descriptionRef.current && onCardClick) {
            onCardClick({
                id,
                card_name: descriptionRef.current.card_name || '',
                primary_meaning: descriptionRef.current.primary_meaning || '',
                interpretation: descriptionRef.current.interpretation || ''
            });
        }
    };

    const cardName = getCardName(id);
    const showCardName = shouldShowCardName(id);

    const reversedStyle = (reversed: boolean) => {
        return !reversed ? 'rotateY(0deg)' : 'rotateY(0deg) rotateZ(180deg)';
    }

    return (
        <motion.div
            className={`relative`}
            onClick={handleCardClick}
            style={{
                width: calculatedWidth,
                height: height,
                perspective: 1000,
                overflow: 'hidden',
                cursor: visible ? 'pointer' : 'default',
                opacity: opacity ? opacity : (isAppeared ? 1 : 0),
                transform: reversedStyle(reversed),
                transformStyle: 'preserve-3d',
            }}
        >
            <motion.div
                animate={{ rotateY: showFront ? 180 : 0 }}
                transition={{ duration: 0.6 }}
                style={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                    transformStyle: 'preserve-3d'
                }}
            >
                {/* Back of card */}
                <motion.div
                    style={{
                        position: 'absolute',
                        width: calculatedWidth,
                        height: height,
                        backfaceVisibility: 'hidden',
                        borderRadius: '5px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.08)',
                        border: '2px solid #f0f0f0',
                        overflow: 'hidden'
                    }}
                >
                    <img
                        src={back}
                        alt="Card back"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',

                            opacity: opacity ? opacity : 1,
                        }}
                    />
                </motion.div>

                {/* Front of card */}
                <motion.div
                    style={{
                        position: 'absolute',
                        width: calculatedWidth - 10,
                        height: height - 10,
                        backfaceVisibility: 'hidden',
                        borderRadius: '5px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.08)',
                        transform: 'rotateY(180deg)',
                        border: '2px solid #f0f0f0',
                        overflow: 'hidden',
                        margin: '5px',
                    }}
                >
                    <img
                        src={cardsImages[id]}
                        alt={cardName || `Tarot card ${id}`}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                            imageRendering: 'high-quality'
                        }}
                    />

                    {/* Card number overlay */}
                    <div style={{
                        position: 'absolute',
                        top: '8px',
                        left: '8px',
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        color: '#333',
                        textShadow: '1px 1px 2px rgba(255,255,255,0.8)',
                        zIndex: 2,
                        display: 'none'
                    }}>
                        {id}
                    </div>

                    {/* Card name overlay at bottom */}
                    {showCardName && cardName && (
                        <div style={{
                            position: 'absolute',
                            bottom: '0',
                            left: '0',
                            right: '0',
                            background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                            padding: '8px 4px 4px 4px',
                            borderRadius: '0 0 14px 14px',
                            display: 'none'
                        }}>
                            <div style={{
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                color: '#ffffff',
                                textAlign: 'center',
                                textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                                lineHeight: '1.1',
                                letterSpacing: '0.5px',
                                display: 'none'
                            }}>
                                {cardName}
                            </div>
                        </div>
                    )}
                </motion.div>
            </motion.div>
        </motion.div>
    );
});

export default CardImage2;